import React, { Fragment, Suspense, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import AlreadyAccepted from "../../../statusPages/AlreadyAccepted";
import AlreadyDeclined from "../../../statusPages/AlreadyDeclined";
import { APP_CONSTANTS } from "../../../../core/config/appConstants";
import { CaseInfoStatusEnum } from "../../enum/caseInfoStatusEnum";
import ChooseAnotherAttribute from "../../../statusPages/ChooseAnotherAttribute";
import { CookieUtils } from "../../../../shared/utils";
import { declineVerification } from "../../slices/verificationDeclineSlice";
import DeniedByUser from "../../../statusPages/DeniedByUser";
import { fetchVerificationOptions } from "../../slices/verificationMethodSelectionSlice";
import NotFound from "../../../statusPages/SessionExpired";
import OutOfTime from "../../../statusPages/outOfTime/OutOfTime";
import SuccessfulVerification from "../../../statusPages/successfulVerification/SuccessfulVerification";
import TechnicalError from "../../../statusPages/TechnicalError";
import TooManyTries from "../../../statusPages/TooManyTries";
import { useAxios } from "../../../../core/api/useAxios";
import useTitle from "../../../../core/hooks/useTitle";
import { VerificationErrorTypeEnum } from "../../enum/verificationErrorTypeEnum";
import { VerificationMethodsEnum } from "../../enum/verificationMethodsEnum";
import { VerificationMethodStatusEnum } from "../../enum/verificationMethodStatusEnum";

const BankAccountNumber = React.lazy(() =>
	import(
		/* webpackPrefetch: true */ "../bankAccountNumber/BankAccountNumber"
	)
);
const Birthdate = React.lazy(() =>
	import(/* webpackPrefetch: true */ "../birthdate/Birthdate")
);
const CustomerAccountNumber = React.lazy(() =>
	import(
		/* webpackPreload: true */ "../customerAccountNumber/CustomerAccountNumber"
	)
);
const SMSNavigation = React.lazy(() =>
	import(/* webpackPrefetch: true */ "../smsNavigation/SMSNavigation")
);
const PostCode = React.lazy(() =>
	import(/* webpackPrefetch: true */ "../postCode/PostCode")
);
const VerificationMethodSelection = React.lazy(() =>
	import(
		/* webpackPreload: true */ "../verificationMethodSelection/VerificationMethodSelection"
	)
);

const Home = () => {
	useTitle("Merkmal auswählen");
	const location = useLocation();
	const { caseInfo, caseInfoError } = useSelector(
		(state) => state.caseInfo
	);
	const { verificationDeclineResponse, declineVerificationError } =
		useSelector((state) => state.verificationDecline);
	const dispatch = useDispatch();
	const axiosClient = useAxios();

	const [verificationState, setVerificationState] = useState({
		verificationMethod: null,
		status: VerificationMethodStatusEnum.PENDING,
		errorType: null
	});

	const isDeclineRequest =
		useSelector(
			(state) => state?.verificationAction?.result ?? null
		) === CaseInfoStatusEnum.DECLINED.toLowerCase();

	const caseID = CookieUtils.getCookie(
		APP_CONSTANTS.STORAGE_CONSTANTS.CASE_ID
	);

	const handleStatusPagesRedirection = (status) => {
		if (status === CaseInfoStatusEnum.AUTHENTICATED) {
			setVerificationState({
				verificationMethod: null,
				status: VerificationMethodStatusEnum.FAILED,
				errorType: VerificationErrorTypeEnum.ALREADY_ACCEPTED
			});
		} else if (
			status === CaseInfoStatusEnum.AUTHENTICATION_FAILED
		) {
			if (caseInfo?.verificationMethod?.length === 0) {
				setVerificationState({
					verificationMethod: null,
					status: VerificationMethodStatusEnum.EXHAUSTED,
					errorType: VerificationErrorTypeEnum.ALL_METHODS_EXHAUSTED
				});
			} else {
				setVerificationState({
					verificationMethod: null,
					status: VerificationMethodStatusEnum.FAILED,
					errorType: VerificationErrorTypeEnum.OUT_OF_TIME
				});
			}
		} else if (status === CaseInfoStatusEnum.DECLINED) {
			setVerificationState({
				verificationMethod: null,
				status: VerificationMethodStatusEnum.FAILED,
				errorType: VerificationErrorTypeEnum.ALREADY_DECLINED
			});
		} else if (status === CaseInfoStatusEnum.NOT_FOUND) {
			setVerificationState({
				verificationMethod: null,
				status: VerificationMethodStatusEnum.FAILED,
				errorType: VerificationErrorTypeEnum.NOT_FOUND
			});
		} else {
			setVerificationState({
				verificationMethod: null,
				status: VerificationMethodStatusEnum.FAILED,
				errorType: VerificationErrorTypeEnum.TECHNICAL_ERROR
			});
		}
	};

	useEffect(() => {
		// Preventing api calls if there is query params in the url
		if (location.search) return;

		if (!caseID) {
			handleStatusPagesRedirection(
				CaseInfoStatusEnum.NOT_FOUND
			);
			return;
		}

		const action = isDeclineRequest
			? declineVerification({ caseId: caseID, axiosClient })
			: fetchVerificationOptions({
					caseId: caseID,
					axiosClient
			  });

		dispatch(action).then((res) => {
			const isRejected = res.type.endsWith("rejected");

			if (isRejected) {
				const errorStatus =
					res?.payload?.status ||
					res?.error?.message ||
					CaseInfoStatusEnum.NOT_FOUND;
				handleStatusPagesRedirection(errorStatus);
			} else if (isDeclineRequest) {
				setVerificationState({
					verificationMethod: null,
					status: VerificationMethodStatusEnum.SUCCESS,
					errorType: VerificationErrorTypeEnum.DENIED_BY_USER
				});
			}
		});
	}, [dispatch, caseID, isDeclineRequest, location.search]);

	const withViewTransition = (callback) => {
		if (document.startViewTransition) {
			document.startViewTransition(() => callback());
		} else {
			callback();
		}
	};

	const handleVerificationMethodClick = (key) => {
		withViewTransition(() => {
			setVerificationState({
				verificationMethod: key,
				status: VerificationMethodStatusEnum.PENDING,
				errorType: null
			});
		});
	};

	const handleBackButton = () => {
		withViewTransition(() => {
			setVerificationState({
				verificationMethod: null,
				status: VerificationMethodStatusEnum.PENDING,
				errorType: null
			});

			dispatch(
				fetchVerificationOptions({
					caseId: caseID,
					axiosClient,
					isDeclined: false
				})
			);
		});
	};

	const updateVerificationState = (newState) => {
		withViewTransition(() => {
			setVerificationState(newState);
		});
	};

	const renderVerificationComponent = () => {
		switch (verificationState.verificationMethod) {
			case VerificationMethodsEnum.BIRTH_DATE:
				return (
					<Birthdate
						handleBackButton={
							handleBackButton
						}
						updateVerificationState={
							updateVerificationState
						}
					/>
				);
			case VerificationMethodsEnum.ZIP_CODE:
				return (
					<PostCode
						handleBackButton={
							handleBackButton
						}
						updateVerificationState={
							updateVerificationState
						}
					/>
				);
			case VerificationMethodsEnum.IBAN:
				return (
					<BankAccountNumber
						handleBackButton={
							handleBackButton
						}
						updateVerificationState={
							updateVerificationState
						}
					/>
				);
			case VerificationMethodsEnum.CUSTOMER_NUMBER:
				return (
					<CustomerAccountNumber
						handleBackButton={
							handleBackButton
						}
						updateVerificationState={
							updateVerificationState
						}
					/>
				);
			case VerificationMethodsEnum.SMS:
				return (
					<SMSNavigation
						goBack={handleBackButton}
						updateVerificationState={
							updateVerificationState
						}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<Suspense fallback={null}>
			<Fragment>
				<div>
					{((verificationState.status ===
						VerificationMethodStatusEnum.PENDING &&
						caseInfo.status ===
							CaseInfoStatusEnum.CREATED) ||
						verificationState.errorType ===
							VerificationErrorTypeEnum.BAD_REQUEST) && (
						<div>
							{!verificationState.verificationMethod &&
								caseInfo
									?.verificationMethod
									?.length >
									0 && (
									<VerificationMethodSelection
										handleClick={
											handleVerificationMethodClick
										}
									/>
								)}
							{!verificationState.verificationMethod &&
								caseInfo
									?.verificationMethod
									?.length ===
									0 && (
									<TooManyTries />
								)}

							{verificationState.verificationMethod &&
								renderVerificationComponent()}
						</div>
					)}
				</div>

				{verificationState.status ===
					VerificationMethodStatusEnum.SUCCESS && (
					<div>
						{verificationState.errorType ==
							null && (
							<SuccessfulVerification />
						)}
						{verificationState.errorType ===
							VerificationErrorTypeEnum.DENIED_BY_USER && (
							<DeniedByUser
								email={
									verificationDeclineResponse?.emailId ||
									""
								}
							/>
						)}
					</div>
				)}

				{verificationState.status ===
					VerificationMethodStatusEnum.FAILED && (
					<div>
						{verificationState.errorType ===
							VerificationErrorTypeEnum.OUT_OF_TIME && (
							<OutOfTime />
						)}
						{verificationState.errorType ===
							VerificationErrorTypeEnum.ALREADY_ACCEPTED && (
							<AlreadyAccepted
								email={
									caseInfo?.emailId ||
									""
								}
							/>
						)}
						{verificationState.errorType ===
							VerificationErrorTypeEnum.ALREADY_DECLINED && (
							<AlreadyDeclined
								email={
									declineVerificationError?.emailId ||
									""
								}
							/>
						)}
						{verificationState.errorType ===
							VerificationErrorTypeEnum.TECHNICAL_ERROR && (
							<TechnicalError />
						)}
						{verificationState.errorType ===
							VerificationErrorTypeEnum.NOT_FOUND && (
							<NotFound />
						)}
					</div>
				)}

				{verificationState.status ===
					VerificationMethodStatusEnum.EXHAUSTED &&
					verificationState.errorType ===
						VerificationErrorTypeEnum.SELECTED_METHOD_EXHAUSTED && (
						<div>
							{caseInfo
								?.verificationMethod
								?.length >
								0 && (
								<ChooseAnotherAttribute
									goBack={
										handleBackButton
									}
								/>
							)}
						</div>
					)}
			</Fragment>
		</Suspense>
	);
};

export default Home;
